import { createSignal, onMount, onCleanup, createEffect, Show, For, createMemo } from "solid-js";
import * as d3 from "d3";
import { persistantStore } from "../../../../../store/persistantStore";
import { sourcesStore } from "../../../../../store/sourcesStore";
import { apiEndpoints } from "@config/env";
import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../../utils/logging";
import { log, debug as logDebug, error as logError } from "../../../../../utils/console";
import Loading from "../../../../../components/utilities/Loading";
import DropDownButton from "../../../../../components/buttons/DropDownButton";
import CheatSheetSettings from "../../../../../components/menus/CheatSheetSettings";
import {
  appendCheatSheetMetadataFiltersToUrl,
  loadCheatSheetFiltersFromSession,
  saveCheatSheetFiltersToSession,
  splitMetadataFilterTokens,
} from "../../../../../utils/metadataFilterTokens";
import { useDashboardShareRestore } from "../../../../../hooks/useDashboardShareRestore";
import { buildCheatSheetShareState, applyCheatSheetShareState } from "../../../../../utils/reportShareState";
import {
  GP50_STRAIGHT_LINE_API_HEADERS,
  GP50_STRAIGHT_LINE_METRIC_OPTIONS,
  GP50_STRAIGHT_LINE_TABLE_COLUMN_KEYS,
} from "../../../../../utils/gp50StraightLineCheatSheetChannels";
import {
  CHEAT_SHEET_CONFIG_ALL,
  cheatSheetConfigModeColumnHeader,
  extractConfigurationOptionsFromRows,
  isCheatSheetConfigFilterMode,
  mapConfigModeKeysUsingChannelOrder,
} from "../../../../../utils/gp50CheatSheetConfigMode";
import {
  CONFIG_CHEAT_SHEET_PAGE_TITLE,
  CONFIG_CHEAT_SHEET_PAGE_DESCRIPTION,
} from "../../../helpers/cheatSheetColumnLabels";
import { exportCheatSheetExcel, type CheatSheetAccelMode } from "../../../helpers/cheatSheetExcel";
import { user } from "../../../../../store/userStore";
import { isAdministrator } from "../../../../../utils/userPermissions";

const { selectedClassName, selectedProjectId, selectedSourceId } = persistantStore;

/** TWS options for group by Channel (single value; backend uses tws ± 2.5 for band). Default = 30. */
const TWS_OPTIONS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const TWS_DEFAULT_INDEX = 4; // 30

/** Metric options for group by Wind (display label -> API value); first = default BSP */
const METRIC_OPTIONS = GP50_STRAIGHT_LINE_METRIC_OPTIONS;

/** Custom column headers when grouping by Channel (API key -> display label). */
const CHANNEL_COLUMN_HEADERS: Record<string, string> = GP50_STRAIGHT_LINE_API_HEADERS;

type GroupBy = "channel" | "wind";
type LegType = "upwind" | "downwind" | "reaching";
type DerivedManeuverType = "tack" | "gybe";

/** Accelerations cheat sheet: fixed column order and display headers. */
const ACCELERATIONS_COLUMN_KEYS = [
  "config",
  "vmg_perc_build",
  "bsp_accmax",
  "twa_accmax",
  "cant_accmax",
  "cant_eff_accmax",
  "pitch_accmax",
  "heel_accmax",
  "jib_sheet_pct_accmax",
  "jib_lead_ang_accmax",
  "jib_cunno_load_accmax",
  "wing_clew_pos_accmax",
  "wing_twist_accmax",
  "wing_ca1_accmax",
  "rake_accmax",
  "rud_rake_accmax",
  "rud_diff_accmax",
  "record_count",
] as const;

const ACCELERATIONS_COLUMN_HEADERS: Record<string, string> = {
  config: "CONFIG",
  vmg_perc_build: "VMG [%]",
  bsp_accmax: "BSP [KPH]",
  twa_accmax: "TWA [DEG]",
  cant_accmax: "DB CANT [DEG]",
  cant_eff_accmax: "DB CANT EFF [DEG]",
  pitch_accmax: "PITCH [DEG]",
  heel_accmax: "HEEL [DEG]",
  jib_sheet_pct_accmax: "JIB SHT [%]",
  jib_lead_ang_accmax: "JIB LEAD [DEG]",
  jib_cunno_load_accmax: "JIB CUNNO [KGF]",
  wing_clew_pos_accmax: "WING CLEW [MM]",
  wing_twist_accmax: "WING TWIST [DEG]",
  wing_ca1_accmax: "WING CA1 [DEG]",
  rake_accmax: "RAKE [DEG]",
  rud_rake_accmax: "RUD RAKE [DEG]",
  rud_diff_accmax: "RUD DIFF [DEG]",
  record_count: "Count",
};

const ACCELERATIONS_METRIC_OPTIONS: { label: string; value: string }[] = ACCELERATIONS_COLUMN_KEYS.filter(
  (k) => k !== "config" && k !== "record_count"
).map((value) => ({
  value,
  label: ACCELERATIONS_COLUMN_HEADERS[value],
}));

function orderAccelerationsTableKeys(keys: string[]): string[] {
  const lower = new Set(keys.map((k) => k.toLowerCase()));
  const ordered = ACCELERATIONS_COLUMN_KEYS.filter((k) => lower.has(k));
  const extra = keys.filter((k) => !ACCELERATIONS_COLUMN_KEYS.includes(k as (typeof ACCELERATIONS_COLUMN_KEYS)[number]));
  return [...ordered, ...extra];
}

/** Maneuver cheat sheet: fixed column order when grouping by Channel. */
const MANEUVER_COLUMN_KEYS = [
  "config",
  "bsp_drop",
  "twa_drop",
  "drop_time",
  "turn_rate_max",
  "bsp_min",
  "bsp_min_delta",
  "twa_exit",
  "turn_angle_max",
  "rake_aoa_min_old_turn",
  "overshoot_angle",
  "raise_time",
  "time_two_boards",
  "twa_build",
  "loss_inv_tgt",
  "loss_turn_tgt",
  "loss_build_tgt",
  "loss_total_tgt",
  "vmg_perc_avg",
  "record_count",
] as const;

/** Maneuver cheat sheet: metric options for group by Wind (display label -> API value). */
const MANEUVER_METRIC_OPTIONS: { label: string; value: string }[] = [
  { label: "Drop time [S]", value: "drop_time" },
  { label: "BSP drop [KPH]", value: "bsp_drop" },
  { label: "TWA drop [DEG]", value: "twa_drop" },
  { label: "Turn rate max [DEG/SEC]", value: "turn_rate_max" },
  { label: "BSP min [KPH]", value: "bsp_min" },
  { label: "BSP min delta [KPH]", value: "bsp_min_delta" },
  { label: "TWA exit [DEG]", value: "twa_exit" },
  { label: "Turn angle max [DEG]", value: "turn_angle_max" },
  { label: "Rake AOA min [DEG]", value: "rake_aoa_min_old_turn" },
  { label: "Overshoot angle [DEG]", value: "overshoot_angle" },
  { label: "Raise time [S]", value: "raise_time" },
  { label: "Two board time [S]", value: "time_two_boards" },
  { label: "TWA build [DEG]", value: "twa_build" },
  { label: "Loss inv [M]", value: "loss_inv_tgt" },
  { label: "Loss turn [M]", value: "loss_turn_tgt" },
  { label: "Loss build [M]", value: "loss_build_tgt" },
  { label: "Loss total [M]", value: "loss_total_tgt" },
  { label: "VMG [%]", value: "vmg_perc_avg" },
];

/** Maneuver cheat sheet: column headers when grouping by Channel. */
const MANEUVER_CHANNEL_COLUMN_HEADERS: Record<string, string> = {
  config: "CONFIG",
  bsp_drop: "BSP DROP [KPH]",
  twa_drop: "TWA DROP [DEG]",
  drop_time: "DROP TIME [S]",
  turn_rate_max: "TURN RATE MAX [DEG/SEC]",
  bsp_min: "BSP MIN [KPH]",
  bsp_min_delta: "BSP MIN DELTA [KPH]",
  twa_exit: "TWA EXIT [DEG]",
  turn_angle_max: "TURN ANGLE MAX [DEG]",
  rake_aoa_min_old_turn: "RAKE AOA MIN [DEG]",
  overshoot_angle: "OVERSHOOT ANGLE [DEG]",
  raise_time: "RAISE TIME [S]",
  time_two_boards: "TWO BOARD TIME [S]",
  twa_build: "TWA BUILD [DEG]",
  loss_inv_tgt: "LOSS INV [M]",
  loss_turn_tgt: "LOSS TURN [M]",
  loss_build_tgt: "LOSS BUILD [M]",
  loss_total_tgt: "LOSS TOTAL [M]",
  vmg_perc_avg: "VMG [%]",
  record_count: "Count",
};

function orderManeuverTableKeys(keys: string[]): string[] {
  const lower = new Set(keys.map((k) => k.toLowerCase()));
  const ordered = MANEUVER_COLUMN_KEYS.filter((k) => lower.has(k));
  const extra = keys.filter((k) => !MANEUVER_COLUMN_KEYS.includes(k as (typeof MANEUVER_COLUMN_KEYS)[number]));
  return [...ordered, ...extra];
}

/** Put CONFIG first, numeric/bin columns in key order, record_count last. */
function orderCheatSheetTableKeys(keys: string[]): string[] {
  const isWindBinMode = keys.some((k) => /^\d+$/.test(String(k).trim()));
  if (isWindBinMode) {
    const recordKey = keys.find((k) => k.toLowerCase() === "record_count");
    const withoutRecord = keys.filter((k) => k.toLowerCase() !== "record_count");
    const configKey = withoutRecord.find((k) => k.toLowerCase() === "config");
    const rest = withoutRecord.filter((k) => k.toLowerCase() !== "config");
    const base = configKey != null ? [configKey, ...rest] : withoutRecord;
    return recordKey != null ? [...base, recordKey] : base;
  }
  const keyByLower = new Map(keys.map((k) => [k.toLowerCase(), k]));
  const ordered = GP50_STRAIGHT_LINE_TABLE_COLUMN_KEYS.filter((k) => keyByLower.has(k)).map(
    (k) => keyByLower.get(k)!
  );
  const used = new Set(ordered.map((k) => k.toLowerCase()));
  const extra = keys.filter((k) => !used.has(k.toLowerCase()));
  return [...ordered, ...extra];
}

function formatNum(value: number): string {
  if (Number.isNaN(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  return rounded === value ? String(value) : rounded.toFixed(1);
}

function formatRecordCount(value: unknown): string {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n));
}

function filterTableKeys(keys: string[]): string[] {
  return keys.filter((k) => k !== undefined && k !== null && String(k).trim() !== "");
}

function isTableRowLabelColumn(key: string, configFilterMode: boolean): boolean {
  const kl = key.toLowerCase();
  if (configFilterMode) return kl === "tws";
  return kl === "config";
}

function isTableValueColumn(key: string, configFilterMode: boolean): boolean {
  const kl = key.toLowerCase();
  if (kl === "record_count") return false;
  return !isTableRowLabelColumn(key, configFilterMode);
}

export default function CheatSheetPage() {
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [rows, setRows] = createSignal<Record<string, unknown>[]>([]);
  const [groupBy, setGroupBy] = createSignal<GroupBy>("channel");
  const [twsIndex, setTwsIndex] = createSignal(TWS_DEFAULT_INDEX); // 30 default
  const [selectedMetricKey, setSelectedMetricKey] = createSignal<string>("bsp"); // first in METRIC_OPTIONS
  const [legType, setLegType] = createSignal<LegType>("upwind");
  const [sortConfig, setSortConfig] = createSignal<{ key: string; direction: "asc" | "desc" }>({ key: "", direction: "asc" });
  const [columnScales, setColumnScales] = createSignal<Record<string, string>>({});
  const [formattedColumns, setFormattedColumns] = createSignal<string[]>([]);
  const [showDeltas, setShowDeltas] = createSignal(true);
  const [isHovered, setIsHovered] = createSignal(false);
  const [showCopy, setShowCopy] = createSignal(false);
  const [copySuccess, setCopySuccess] = createSignal(false);

  // Maneuvers section state
  const [loadingManeuver, setLoadingManeuver] = createSignal(true);
  const [errorManeuver, setErrorManeuver] = createSignal<string | null>(null);
  const [rowsManeuver, setRowsManeuver] = createSignal<Record<string, unknown>[]>([]);
  const [groupByManeuver, setGroupByManeuver] = createSignal<GroupBy>("channel");
  const [selectedMetricKeyManeuver, setSelectedMetricKeyManeuver] = createSignal<string>("drop_time");
  const [sortConfigManeuver, setSortConfigManeuver] = createSignal<{ key: string; direction: "asc" | "desc" }>({ key: "", direction: "asc" });
  const [columnScalesManeuver, setColumnScalesManeuver] = createSignal<Record<string, string>>({});
  const [formattedColumnsManeuver, setFormattedColumnsManeuver] = createSignal<string[]>([]);
  const [showDeltasManeuver, setShowDeltasManeuver] = createSignal(true);
  const [isHoveredManeuver, setIsHoveredManeuver] = createSignal(false);
  const [showCopyManeuver, setShowCopyManeuver] = createSignal(false);
  const [copySuccessManeuver, setCopySuccessManeuver] = createSignal(false);

  const [loadingAccelerations, setLoadingAccelerations] = createSignal(true);
  const [errorAccelerations, setErrorAccelerations] = createSignal<string | null>(null);
  const [rowsAccelerations, setRowsAccelerations] = createSignal<Record<string, unknown>[]>([]);
  const [sortConfigAccelerations, setSortConfigAccelerations] = createSignal<{ key: string; direction: "asc" | "desc" }>({
    key: "vmg_perc_build",
    direction: "desc",
  });
  const [columnScalesAccelerations, setColumnScalesAccelerations] = createSignal<Record<string, string>>({});
  const [formattedColumnsAccelerations, setFormattedColumnsAccelerations] = createSignal<string[]>([]);
  const [showDeltasAccelerations, setShowDeltasAccelerations] = createSignal(true);
  const [isHoveredAccelerations, setIsHoveredAccelerations] = createSignal(false);
  const [showCopyAccelerations, setShowCopyAccelerations] = createSignal(false);
  const [copySuccessAccelerations, setCopySuccessAccelerations] = createSignal(false);
  const [groupByAccelerations, setGroupByAccelerations] = createSignal<GroupBy>("channel");
  const [selectedMetricKeyAccelerations, setSelectedMetricKeyAccelerations] = createSignal<string>("vmg_perc_build");
  const [selectedConfiguration, setSelectedConfiguration] = createSignal<string>(CHEAT_SHEET_CONFIG_ALL);
  const [configurationOptions, setConfigurationOptions] = createSignal<string[]>([]);
  // Which Excel export is currently running (null = idle); disables both buttons while one runs.
  const [excelMode, setExcelMode] = createSignal<CheatSheetAccelMode | null>(null);
  const [printError, setPrintError] = createSignal<string | null>(null);

  // Year/Event metadata contains-filters (same behaviour as Performance History), applied to all tables.
  const [filterYear, setFilterYear] = createSignal<string>("");
  const [filterEvent, setFilterEvent] = createSignal<string>("");
  // Foiling-state filter (H0/H1/H2, comma-joined). Always non-empty; defaults to H0-only so the
  // cheat sheet is always state-gated. Combined maneuver states (e.g. H0-H1) match via contains.
  const [filterState, setFilterState] = createSignal<string>("H0");

  // Session persistence (shared with FleetCheatSheet, like performanceFilters): filters survive
  // page navigation within the session, but are not saved to persistent user settings.
  const loadFiltersFromSession = () => {
    const saved = loadCheatSheetFiltersFromSession();
    if (saved) {
      setFilterYear(saved.filterYear);
      setFilterEvent(saved.filterEvent);
      setFilterState(saved.filterState);
    }
  };

  const saveFiltersToSession = () => saveCheatSheetFiltersToSession(filterYear(), filterEvent(), filterState());

  /** Append the active year/event/state contains-filters to a cheat-sheet fetch URL as JSON array params. */
  const appendMetadataFiltersToUrl = (url: string): string =>
    appendCheatSheetMetadataFiltersToUrl(url, filterYear(), filterEvent(), filterState());

  const isConfigFilterMode = createMemo(() => isCheatSheetConfigFilterMode(selectedConfiguration()));
  const configurationDropdownOptions = createMemo(() => [CHEAT_SHEET_CONFIG_ALL, ...configurationOptions()]);

  const maneuverType = createMemo((): DerivedManeuverType | null => {
    const leg = legType();
    return leg === "upwind" ? "tack" : leg === "downwind" ? "gybe" : null;
  });
  const showManeuverSections = createMemo(() => maneuverType() != null);

  const CHEAT_SHEET_PAGE_KEY = 'CheatSheet';
  useDashboardShareRestore(
    CHEAT_SHEET_PAGE_KEY,
    () =>
      buildCheatSheetShareState({
        groupBy: groupBy(),
        twsIndex: twsIndex(),
        selectedMetricKey: selectedMetricKey(),
        legType: legType(),
        sortConfig: sortConfig(),
        columnScales: columnScales(),
        formattedColumns: formattedColumns(),
        showDeltas: showDeltas(),
        groupByManeuver: groupByManeuver(),
        selectedMetricKeyManeuver: selectedMetricKeyManeuver(),
        sortConfigManeuver: sortConfigManeuver(),
        columnScalesManeuver: columnScalesManeuver(),
        formattedColumnsManeuver: formattedColumnsManeuver(),
        showDeltasManeuver: showDeltasManeuver(),
        sortConfigAccelerations: sortConfigAccelerations(),
        columnScalesAccelerations: columnScalesAccelerations(),
        formattedColumnsAccelerations: formattedColumnsAccelerations(),
        showDeltasAccelerations: showDeltasAccelerations(),
        groupByAccelerations: groupByAccelerations(),
        selectedMetricKeyAccelerations: selectedMetricKeyAccelerations(),
        selectedConfiguration: selectedConfiguration(),
        filterYear: filterYear(),
        filterEvent: filterEvent(),
        filterState: filterState(),
      }),
    (obj) =>
      applyCheatSheetShareState(obj, CHEAT_SHEET_PAGE_KEY, {
        setGroupBy,
        setTwsIndex,
        setSelectedMetricKey,
        setLegType,
        setSortConfig,
        setColumnScales,
        setFormattedColumns,
        setShowDeltas,
        setGroupByManeuver,
        setSelectedMetricKeyManeuver,
        setSortConfigManeuver,
        setColumnScalesManeuver,
        setFormattedColumnsManeuver,
        setShowDeltasManeuver,
        setSortConfigAccelerations,
        setColumnScalesAccelerations,
        setFormattedColumnsAccelerations,
        setShowDeltasAccelerations,
        setGroupByAccelerations,
        setSelectedMetricKeyAccelerations,
        setSelectedConfiguration,
        setFilterYear,
        setFilterEvent,
        setFilterState,
      })
  );

  createEffect(() => {
    if (isHovered()) {
      const timer = setTimeout(() => setShowCopy(true), 2000);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopy(false);
    }
  });

  createEffect(() => {
    if (isHoveredManeuver()) {
      const timer = setTimeout(() => setShowCopyManeuver(true), 2000);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyManeuver(false);
    }
  });

  createEffect(() => {
    if (isHoveredAccelerations()) {
      const timer = setTimeout(() => setShowCopyAccelerations(true), 2000);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyAccelerations(false);
    }
  });

  const getRowValue = (row: Record<string, unknown>, key: string): number | string | null => {
    const v = row[key] ?? row[key.toLowerCase()] ?? row[key.toUpperCase()];
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
    return String(v);
  };

  const tableColumns = createMemo(() => {
    const r = rows();
    if (r.length === 0) return [];
    const keys = filterTableKeys(Object.keys(r[0]));
    if (isConfigFilterMode()) {
      return mapConfigModeKeysUsingChannelOrder(keys, orderCheatSheetTableKeys);
    }
    return orderCheatSheetTableKeys(keys);
  });

  const sortedRows = createMemo(() => {
    const r = rows();
    const { key, direction } = sortConfig();
    if (!key || r.length === 0) return r;
    const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
    return [...r].sort((a, b) => {
      const aV = getVal(a);
      const bV = getVal(b);
      if (aV == null && bV == null) return 0;
      if (aV == null) return direction === "asc" ? 1 : -1;
      if (bV == null) return direction === "asc" ? -1 : 1;
      const cmp = typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
      return direction === "asc" ? cmp : -cmp;
    });
  });

  const valueKeys = createMemo(() =>
    tableColumns().filter((k) => isTableValueColumn(k, isConfigFilterMode()))
  );

  const tableColumnsManeuver = createMemo(() => {
    const r = rowsManeuver();
    if (r.length === 0) return [];
    const keys = filterTableKeys(Object.keys(r[0]));
    if (isConfigFilterMode()) {
      return mapConfigModeKeysUsingChannelOrder(keys, orderManeuverTableKeys);
    }
    return orderManeuverTableKeys(keys);
  });

  const sortedRowsManeuver = createMemo(() => {
    const r = rowsManeuver();
    const { key, direction } = sortConfigManeuver();
    if (!key || r.length === 0) return r;
    const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
    return [...r].sort((a, b) => {
      const aV = getVal(a);
      const bV = getVal(b);
      if (aV == null && bV == null) return 0;
      if (aV == null) return direction === "asc" ? 1 : -1;
      if (bV == null) return direction === "asc" ? -1 : 1;
      const cmp = typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
      return direction === "asc" ? cmp : -cmp;
    });
  });

  const valueKeysManeuver = createMemo(() =>
    tableColumnsManeuver().filter((k) => isTableValueColumn(k, isConfigFilterMode()))
  );

  const tableColumnsAccelerations = createMemo(() => {
    const r = rowsAccelerations();
    if (r.length === 0) return [];
    const keys = filterTableKeys(Object.keys(r[0]));
    if (isConfigFilterMode()) {
      return mapConfigModeKeysUsingChannelOrder(keys, orderAccelerationsTableKeys);
    }
    return groupByAccelerations() === "channel" ? orderAccelerationsTableKeys(keys) : orderCheatSheetTableKeys(keys);
  });

  const sortedRowsAccelerations = createMemo(() => {
    const r = rowsAccelerations();
    const { key, direction } = sortConfigAccelerations();
    if (!key || r.length === 0) return r;
    const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
    return [...r].sort((a, b) => {
      const aV = getVal(a);
      const bV = getVal(b);
      if (aV == null && bV == null) return 0;
      if (aV == null) return direction === "asc" ? 1 : -1;
      if (bV == null) return direction === "asc" ? -1 : 1;
      const cmp = typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
      return direction === "asc" ? cmp : -cmp;
    });
  });

  const valueKeysAccelerations = createMemo(() =>
    tableColumnsAccelerations().filter((k) => isTableValueColumn(k, isConfigFilterMode()))
  );

  const sortAndFormat = (
    rowsList: Record<string, unknown>[],
    key: string,
    current: { key: string; direction: "asc" | "desc" },
    setSort: (c: { key: string; direction: "asc" | "desc" }) => void,
    getScales: () => Record<string, string>,
    setScales: (s: Record<string, string>) => void,
    getFormatted: () => string[],
    setFormatted: (cols: string[]) => void,
    vKeys: string[]
  ): void => {
    const direction = current.key === key && current.direction === "asc" ? "desc" : "asc";
    setSort({ key, direction });
    const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
    if (vKeys.includes(key)) {
      const values = rowsList.map((r) => getVal(r)).filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
      const min = d3.min(values) ?? 0;
      const max = d3.max(values) ?? 0;
      const scaleStr = JSON.stringify({ domain: [min, max], range: [0, 6] });
      setScales({ ...getScales(), [key]: scaleStr });
      const formattedCols = getFormatted();
      setFormatted(formattedCols.includes(key) ? formattedCols : [...formattedCols, key]);
    }
  };

  const recomputeScalesFromData = (
    rowsList: Record<string, unknown>[],
    formattedKeys: string[],
    vKeys: string[]
  ): Record<string, string> => {
    const newScales: Record<string, string> = {};
    for (const key of formattedKeys) {
      if (!vKeys.includes(key) || rowsList.length === 0) continue;
      const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
      const values = rowsList.map((r) => getVal(r)).filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
      const min = d3.min(values) ?? 0;
      const max = d3.max(values) ?? 0;
      newScales[key] = JSON.stringify({ domain: [min, max], range: [0, 6] });
    }
    return newScales;
  };

  const cellClassForScale = (
    row: Record<string, unknown>,
    key: string,
    scales: Record<string, string>,
    formatted: string[]
  ): string => {
    if (!formatted.includes(key) || !scales[key]) return "";
    try {
      const { domain, range } = JSON.parse(scales[key]) as { domain: number[]; range: number[] };
      const scale = d3.scaleLinear().domain(domain).range(range);
      const v = getRowValue(row, key);
      if (v == null || typeof v !== "number" || Number.isNaN(v)) return "";
      const x = scale(v);
      return `c${Math.round(Math.max(0, Math.min(6, x)))}`;
    } catch {
      return "";
    }
  };

  const formatDelta = (delta: number): string => {
    if (Number.isNaN(delta)) return "";
    const n = Number(delta);
    const prefix = n > 0 ? "+" : "";
    return `${prefix}${n.toFixed(1)}`;
  };

  const clearFormatting = () => {
    setFormattedColumns([]);
    setColumnScales({});
    setSortConfig({ key: "", direction: "asc" });
  };

  const clearFormattingManeuver = () => {
    setFormattedColumnsManeuver([]);
    setColumnScalesManeuver({});
    setSortConfigManeuver({ key: "", direction: "asc" });
  };

  const clearFormattingAccelerations = () => {
    setFormattedColumnsAccelerations([]);
    setColumnScalesAccelerations({});
    setSortConfigAccelerations({ key: "vmg_perc_build", direction: "desc" });
  };

  const addAllFormatting = (): void => {
    const rowsList = rows();
    const vKeys = valueKeys();
    if (rowsList.length === 0 || vKeys.length === 0) return;
    setColumnScales(recomputeScalesFromData(rowsList, vKeys, vKeys));
    setFormattedColumns([...vKeys]);
  };

  const toggleFormatting = (): void => {
    if (formattedColumns().length === 0) {
      addAllFormatting();
    } else {
      clearFormatting();
    }
  };

  const addAllFormattingManeuver = (): void => {
    const rowsList = rowsManeuver();
    const vKeys = valueKeysManeuver();
    if (rowsList.length === 0 || vKeys.length === 0) return;
    setColumnScalesManeuver(recomputeScalesFromData(rowsList, vKeys, vKeys));
    setFormattedColumnsManeuver([...vKeys]);
  };

  const toggleFormattingManeuver = (): void => {
    if (formattedColumnsManeuver().length === 0) {
      addAllFormattingManeuver();
    } else {
      clearFormattingManeuver();
    }
  };

  const addAllFormattingAccelerations = (): void => {
    const rowsList = rowsAccelerations();
    const vKeys = valueKeysAccelerations();
    if (rowsList.length === 0 || vKeys.length === 0) return;
    setColumnScalesAccelerations(recomputeScalesFromData(rowsList, vKeys, vKeys));
    setFormattedColumnsAccelerations([...vKeys]);
  };

  const toggleFormattingAccelerations = (): void => {
    if (formattedColumnsAccelerations().length === 0) {
      addAllFormattingAccelerations();
    } else {
      clearFormattingAccelerations();
    }
  };

  const sortData = (key: string) => {
    sortAndFormat(
      rows(),
      key,
      sortConfig(),
      setSortConfig,
      () => columnScales(),
      setColumnScales,
      () => formattedColumns(),
      setFormattedColumns,
      valueKeys()
    );
  };

  const sortDataManeuver = (key: string) => {
    sortAndFormat(
      rowsManeuver(),
      key,
      sortConfigManeuver(),
      setSortConfigManeuver,
      () => columnScalesManeuver(),
      setColumnScalesManeuver,
      () => formattedColumnsManeuver(),
      setFormattedColumnsManeuver,
      valueKeysManeuver()
    );
  };

  const sortDataAccelerations = (key: string) => {
    sortAndFormat(
      rowsAccelerations(),
      key,
      sortConfigAccelerations(),
      setSortConfigAccelerations,
      () => columnScalesAccelerations(),
      setColumnScalesAccelerations,
      () => formattedColumnsAccelerations(),
      setFormattedColumnsAccelerations,
      valueKeysAccelerations()
    );
  };

  const appendSourceFilterToUrl = (url: string, sourceId: number | null | undefined): string => {
    if (sourceId != null && Number(sourceId) > 0) {
      const sourceName = sourcesStore.getSourceName(Number(sourceId));
      if (sourceName != null) {
        return `${url}&source_names=${encodeURIComponent(JSON.stringify([sourceName]))}`;
      }
      return `${url}&source_id=${encodeURIComponent(Number(sourceId))}`;
    }
    return url;
  };

  const normalizeCheatSheetRows = (raw: unknown[]): Record<string, unknown>[] =>
    raw.map((r) => {
      const row = (r && typeof r === "object" ? r : {}) as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(row)) {
        out[k.toLowerCase()] = row[k];
      }
      return out;
    });

  const fetchConfigurationOptions = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const sourceId = selectedSourceId();
    if (!className || !projectId) {
      setConfigurationOptions([]);
      return;
    }
    try {
      const leg = legType();
      // Complete config universe across all three tables (no TWS band / IQR narrowing) so the
      // dropdown is always a superset of what the tables can show.
      let url =
        `${apiEndpoints.app.data}/cheat-sheet-configs?class_name=${encodeURIComponent(className)}` +
        `&project_id=${encodeURIComponent(projectId)}&leg_type=${encodeURIComponent(leg)}`;
      url = appendSourceFilterToUrl(url, sourceId);
      url = appendMetadataFiltersToUrl(url);
      const result = await getData(url);
      if (!result?.success) {
        setConfigurationOptions([]);
        return;
      }
      const payload = result.data as { rows?: unknown[] };
      const raw = Array.isArray(payload?.rows) ? payload.rows : [];
      const options = extractConfigurationOptionsFromRows([normalizeCheatSheetRows(raw)]);
      setConfigurationOptions(options);
      // If the selected configuration disappeared from the filtered options, fall back to ALL
      const selected = selectedConfiguration();
      if (selected !== CHEAT_SHEET_CONFIG_ALL && !options.includes(selected)) {
        setSelectedConfiguration(CHEAT_SHEET_CONFIG_ALL);
      }
    } catch (err) {
      logError("CheatSheet: fetch configuration options failed", err);
      setConfigurationOptions([]);
    }
  };

  const fetchData = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const sourceId = selectedSourceId();
    if (!className || !projectId) {
      setRows([]);
      setError(null);
      return;
    }
    const group = isConfigFilterMode() ? "config" : groupBy();
    const leg = legType();
    const configVal = selectedConfiguration();
    setLoading(true);
    setError(null);
    try {
      const twsVal = group === "channel" ? TWS_OPTIONS[twsIndex()] : null;
      let url = `${apiEndpoints.app.data}/cheat-sheet?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&group_by=${encodeURIComponent(group)}&leg_type=${encodeURIComponent(leg)}`;
      url = appendSourceFilterToUrl(url, sourceId);
      url = appendMetadataFiltersToUrl(url);
      if (group === "channel" && twsVal != null) {
        url += `&tws=${encodeURIComponent(twsVal)}`;
      }
      if (group === "wind") {
        url += `&metric=${encodeURIComponent(selectedMetricKey())}`;
      }
      if (group === "config") {
        url += `&config=${encodeURIComponent(configVal)}`;
      }
      const result = await getData(url);
      if (!result?.success) {
        setError(result?.message ?? "Failed to load cheat sheet data");
        setRows([]);
        return;
      }
      const payload = result.data as { rows?: Record<string, unknown>[] };
      const raw = Array.isArray(payload?.rows) ? payload.rows : [];
      const normalized = normalizeCheatSheetRows(raw);
      setRows(normalized);
      if (group === "channel") {
        setSortConfig({ key: "", direction: "asc" });
      } else if (group === "config") {
        setSortConfig({ key: "tws", direction: "asc" });
      }
      logDebug("CheatSheet: fetched rows", { count: normalized.length, groupBy: group });
    } catch (err) {
      logError("CheatSheet: fetch failed", err);
      setError(err instanceof Error ? err.message : "Failed to load cheat sheet data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchManeuverData = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const sourceId = selectedSourceId();
    if (!className || !projectId) {
      setRowsManeuver([]);
      setErrorManeuver(null);
      return;
    }
    const group = isConfigFilterMode() ? "config" : groupByManeuver();
    const configVal = selectedConfiguration();
    const maneuver = maneuverType();
    if (!maneuver) {
      setRowsManeuver([]);
      setErrorManeuver(null);
      setLoadingManeuver(false);
      return;
    }
    setLoadingManeuver(true);
    setErrorManeuver(null);
    try {
      const twsVal = group === "channel" ? TWS_OPTIONS[twsIndex()] : null;
      let url = `${apiEndpoints.app.data}/maneuver-cheat-sheet?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&group_by=${encodeURIComponent(group)}&maneuver_type=${encodeURIComponent(maneuver)}`;
      url = appendSourceFilterToUrl(url, sourceId);
      url = appendMetadataFiltersToUrl(url);
      if (group === "channel" && twsVal != null) {
        url += `&tws=${encodeURIComponent(twsVal)}`;
      }
      if (group === "wind") {
        url += `&metric=${encodeURIComponent(selectedMetricKeyManeuver())}`;
      }
      if (group === "config") {
        url += `&config=${encodeURIComponent(configVal)}`;
      }
      const result = await getData(url);
      if (!result?.success) {
        setErrorManeuver(result?.message ?? "Failed to load maneuver cheat sheet data");
        setRowsManeuver([]);
        return;
      }
      const payload = result.data as { rows?: Record<string, unknown>[] };
      const raw = Array.isArray(payload?.rows) ? payload.rows : [];
      const normalized = normalizeCheatSheetRows(raw);
      setRowsManeuver(normalized);
      if (group === "config") {
        setSortConfigManeuver({ key: "tws", direction: "asc" });
      }
      logDebug("CheatSheet Maneuvers: fetched rows", { count: normalized.length, groupBy: group });
    } catch (err) {
      logError("CheatSheet Maneuvers: fetch failed", err);
      setErrorManeuver(err instanceof Error ? err.message : "Failed to load maneuver cheat sheet data");
      setRowsManeuver([]);
    } finally {
      setLoadingManeuver(false);
    }
  };

  const fetchAccelerationsData = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const sourceId = selectedSourceId();
    if (!className || !projectId) {
      setRowsAccelerations([]);
      setErrorAccelerations(null);
      return;
    }
    const maneuver = maneuverType();
    if (!maneuver) {
      setRowsAccelerations([]);
      setErrorAccelerations(null);
      setLoadingAccelerations(false);
      return;
    }
    setLoadingAccelerations(true);
    setErrorAccelerations(null);
    try {
      const group = isConfigFilterMode() ? "config" : groupByAccelerations();
      const configVal = selectedConfiguration();
      const twsVal = group === "channel" ? TWS_OPTIONS[twsIndex()] : null;
      let url = `${apiEndpoints.app.data}/maneuver-cheat-sheet?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&section=accelerations&group_by=${encodeURIComponent(group)}&maneuver_type=${encodeURIComponent(maneuver)}`;
      url = appendSourceFilterToUrl(url, sourceId);
      url = appendMetadataFiltersToUrl(url);
      if (group === "channel" && twsVal != null) {
        url += `&tws=${encodeURIComponent(twsVal)}`;
      }
      if (group === "wind") {
        url += `&metric=${encodeURIComponent(selectedMetricKeyAccelerations())}`;
      }
      if (group === "config") {
        url += `&config=${encodeURIComponent(configVal)}`;
      }
      const result = await getData(url);
      if (!result?.success) {
        setErrorAccelerations(result?.message ?? "Failed to load accelerations cheat sheet data");
        setRowsAccelerations([]);
        return;
      }
      const payload = result.data as { rows?: Record<string, unknown>[] };
      const raw = Array.isArray(payload?.rows) ? payload.rows : [];
      const normalized = normalizeCheatSheetRows(raw);
      setRowsAccelerations(normalized);
      if (group === "config") {
        setSortConfigAccelerations({ key: "tws", direction: "asc" });
      } else {
        setSortConfigAccelerations({ key: "vmg_perc_build", direction: "desc" });
      }
      logDebug("CheatSheet Accelerations: fetched rows", { count: normalized.length, maneuver, groupBy: group });
    } catch (err) {
      logError("CheatSheet Accelerations: fetch failed", err);
      setErrorAccelerations(err instanceof Error ? err.message : "Failed to load accelerations cheat sheet data");
      setRowsAccelerations([]);
    } finally {
      setLoadingAccelerations(false);
    }
  };

  onMount(() => {
    logPageLoad("CheatSheet.tsx", "Cheat Sheet", "Loaded");
    // Restore Year/Event filters from sessionStorage (session persistence across page navigation)
    loadFiltersFromSession();
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "CheatSheet",
      scaleToWidth: true,
    });
    onCleanup(() => cleanupScaling());
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    selectedSourceId();
    legType();
    filterYear();
    filterEvent();
    filterState();
    fetchConfigurationOptions();
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    selectedSourceId();
    selectedConfiguration();
    groupBy();
    twsIndex();
    selectedMetricKey();
    legType();
    filterYear();
    filterEvent();
    filterState();
    fetchData();
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    selectedSourceId();
    selectedConfiguration();
    groupByManeuver();
    twsIndex();
    selectedMetricKeyManeuver();
    legType();
    filterYear();
    filterEvent();
    filterState();
    fetchManeuverData();
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    selectedSourceId();
    selectedConfiguration();
    groupByAccelerations();
    twsIndex();
    selectedMetricKeyAccelerations();
    legType();
    filterYear();
    filterEvent();
    filterState();
    fetchAccelerationsData();
  });

  createEffect(() => {
    const rowsList = rows();
    const formatted = formattedColumns();
    const vKeys = valueKeys();
    if (formatted.length === 0 || rowsList.length === 0) return;
    const newScales = recomputeScalesFromData(rowsList, formatted, vKeys);
    if (Object.keys(newScales).length > 0) setColumnScales(newScales);
  });

  createEffect(() => {
    const rowsList = rowsManeuver();
    const formatted = formattedColumnsManeuver();
    const vKeys = valueKeysManeuver();
    if (formatted.length === 0 || rowsList.length === 0) return;
    const newScales = recomputeScalesFromData(rowsList, formatted, vKeys);
    if (Object.keys(newScales).length > 0) setColumnScalesManeuver(newScales);
  });

  createEffect(() => {
    const rowsList = rowsAccelerations();
    const formatted = formattedColumnsAccelerations();
    const vKeys = valueKeysAccelerations();
    if (formatted.length === 0 || rowsList.length === 0) return;
    const newScales = recomputeScalesFromData(rowsList, formatted, vKeys);
    if (Object.keys(newScales).length > 0) setColumnScalesAccelerations(newScales);
  });

  const legDisplay = (): string => {
    const leg = legType();
    return leg === "reaching" ? "REACH" : leg === "upwind" ? "UPWIND" : "DOWNWIND";
  };

  const maneuversSectionTitle = (): string => {
    const leg = legType();
    if (leg === "downwind") return "Maneuvers - Gybes";
    if (leg === "upwind") return "Maneuvers - Tacks";
    return "Maneuvers";
  };

  const accelerationsSectionTitle = (): string => {
    const leg = legType();
    if (leg === "downwind") return "Accelerations - Gybes";
    if (leg === "upwind") return "Accelerations - Tacks";
    return "Accelerations";
  };

  const twsLabel = (): string => String(TWS_OPTIONS[twsIndex()] ?? 30);

  const getColumnHeaderAccelerations = (key: string): string => {
    if (isConfigFilterMode()) {
      return cheatSheetConfigModeColumnHeader(key, ACCELERATIONS_COLUMN_HEADERS);
    }
    if (key.toLowerCase() === "record_count") return "Count";
    if (groupByAccelerations() === "channel") {
      return ACCELERATIONS_COLUMN_HEADERS[key.toLowerCase()] ?? key;
    }
    if (key.toLowerCase() === "config") return "CONFIG";
    return key;
  };

  /** Display label for table column: use CHANNEL_COLUMN_HEADERS when grouping by channel, else key as-is (wind: config -> CONFIG, bin numbers stay). */
  const getColumnHeader = (key: string): string => {
    if (isConfigFilterMode()) {
      return cheatSheetConfigModeColumnHeader(key, CHANNEL_COLUMN_HEADERS);
    }
    if (key.toLowerCase() === "record_count") return "Count";
    if (groupBy() === "channel") {
      return CHANNEL_COLUMN_HEADERS[key.toLowerCase()] ?? key;
    }
    if (key.toLowerCase() === "config") return "CONFIG";
    return key;
  };

  /** Maneuvers section: column header for table. */
  const getColumnHeaderManeuver = (key: string): string => {
    if (isConfigFilterMode()) {
      return cheatSheetConfigModeColumnHeader(key, MANEUVER_CHANNEL_COLUMN_HEADERS);
    }
    if (key.toLowerCase() === "record_count") return "Count";
    if (groupByManeuver() === "channel") {
      return MANEUVER_CHANNEL_COLUMN_HEADERS[key.toLowerCase()] ?? key;
    }
    if (key.toLowerCase() === "config") return "CONFIG";
    return key;
  };

  /** Copy table to clipboard (values only, no deltas). */
  const copyTableToClipboard = async () => {
    try {
      const cols = tableColumns();
      const rowsList = sortedRows();
      let clipboardText = cols.map((k) => getColumnHeader(k)).join("\t") + "\n";
      for (const row of rowsList) {
        const cells = cols.map((key) => {
          const val = getRowValue(row, key);
          if (val == null) return "—";
          if (isTableRowLabelColumn(key, isConfigFilterMode())) return String(val);
          if (key.toLowerCase() === "record_count") return formatRecordCount(val);
          return typeof val === "number" ? formatNum(val) : String(val);
        });
        clipboardText += cells.join("\t") + "\n";
      }
      log("CheatSheet: copy table to clipboard", { textLength: clipboardText.length });
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        const ta = document.createElement("textarea");
        ta.value = clipboardText;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err: unknown) {
      logError("CheatSheet: copy table failed", err);
    }
  };

  /** Copy maneuver table to clipboard. */
  const copyTableToClipboardManeuver = async () => {
    try {
      const cols = tableColumnsManeuver();
      const rowsList = sortedRowsManeuver();
      let clipboardText = cols.map((k) => getColumnHeaderManeuver(k)).join("\t") + "\n";
      for (const row of rowsList) {
        const cells = cols.map((key) => {
          const val = getRowValue(row, key);
          if (val == null) return "—";
          if (isTableRowLabelColumn(key, isConfigFilterMode())) return String(val);
          if (key.toLowerCase() === "record_count") return formatRecordCount(val);
          return typeof val === "number" ? formatNum(val) : String(val);
        });
        clipboardText += cells.join("\t") + "\n";
      }
      log("CheatSheet Maneuvers: copy table to clipboard", { textLength: clipboardText.length });
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
        setCopySuccessManeuver(true);
        setTimeout(() => setCopySuccessManeuver(false), 2000);
      } else {
        const ta = document.createElement("textarea");
        ta.value = clipboardText;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopySuccessManeuver(true);
        setTimeout(() => setCopySuccessManeuver(false), 2000);
      }
    } catch (err: unknown) {
      logError("CheatSheet Maneuvers: copy table failed", err);
    }
  };

  const copyTableToClipboardAccelerations = async () => {
    try {
      const cols = tableColumnsAccelerations();
      const rowsList = sortedRowsAccelerations();
      let clipboardText = cols.map((k) => getColumnHeaderAccelerations(k)).join("\t") + "\n";
      for (const row of rowsList) {
        const cells = cols.map((key) => {
          const val = getRowValue(row, key);
          if (val == null) return "—";
          if (isTableRowLabelColumn(key, isConfigFilterMode())) return String(val);
          if (key.toLowerCase() === "record_count") return formatRecordCount(val);
          return typeof val === "number" ? formatNum(val) : String(val);
        });
        clipboardText += cells.join("\t") + "\n";
      }
      log("CheatSheet Accelerations: copy table to clipboard", { textLength: clipboardText.length });
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
        setCopySuccessAccelerations(true);
        setTimeout(() => setCopySuccessAccelerations(false), 2000);
      } else {
        const ta = document.createElement("textarea");
        ta.value = clipboardText;
        ta.className = "cheat-sheet-copy-textarea-offscreen";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopySuccessAccelerations(true);
        setTimeout(() => setCopySuccessAccelerations(false), 2000);
      }
    } catch (err: unknown) {
      logError("CheatSheet Accelerations: copy table failed", err);
    }
  };

  const handleExportCheatSheetExcel = async (mode: CheatSheetAccelMode) => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId || !isConfigFilterMode() || excelMode() != null) return;
    setExcelMode(mode);
    setPrintError(null);
    const sourceId = selectedSourceId();
    const sourceName =
      sourceId != null && Number(sourceId) > 0 ? sourcesStore.getSourceName(Number(sourceId)) : null;
    const result = await exportCheatSheetExcel(
      {
        className,
        projectId,
        configName: selectedConfiguration(),
        appendSourceFilter: (url) => appendSourceFilterToUrl(url, sourceId),
        appendMetadataFilters: appendMetadataFiltersToUrl,
        filterSummary: {
          years: splitMetadataFilterTokens(filterYear()),
          events: splitMetadataFilterTokens(filterEvent()),
          sources: sourceName != null ? [sourceName] : [],
        },
        logPrefix: "CheatSheet",
      },
      { mode }
    );
    setExcelMode(null);
    if (!result.success && result.message) setPrintError(result.message);
    const label = mode === "delta" ? "Excel (delta)" : "Excel";
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Exported cheat sheet ${label} for ${selectedConfiguration()}`);
  };

  return (
    <div id="media-container" class="cheat-sheet-page">
      <div class="container relative">
        <div class="performance-charts-scroll-container">
          <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner">
            <header class="cheat-sheet-page-header">
              <h1 class="report-page-title cheat-sheet-title">{CONFIG_CHEAT_SHEET_PAGE_TITLE}</h1>
              <p class="cheat-sheet-page-description">{CONFIG_CHEAT_SHEET_PAGE_DESCRIPTION}</p>
            </header>

            <div class="cheat-sheet-top-controls" style="display: flex; align-items: flex-start; gap: 12px;">
              <div class="cheat-sheet-settings-parent settings-parent" style="display: flex; align-items: center; margin-left: auto; order: 2;">
                <CheatSheetSettings
                  filterYear={() => filterYear()}
                  onFilterYearChange={(value) => {
                    setFilterYear(value);
                    saveFiltersToSession();
                  }}
                  filterEvent={() => filterEvent()}
                  onFilterEventChange={(value) => {
                    setFilterEvent(value);
                    saveFiltersToSession();
                  }}
                  filterState={() => filterState()}
                  onFilterStateChange={(value) => {
                    setFilterState(value);
                    saveFiltersToSession();
                  }}
                />
              </div>
              <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
                <DropDownButton
                  options={configurationDropdownOptions()}
                  defaultText={selectedConfiguration()}
                  smallLabel="Configuration"
                  size="big"
                  handleSelection={async (item) => {
                    setSelectedConfiguration(item);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Configuration changed to ${item}`);
                  }}
                />
                <DropDownButton
                  options={["REACH", "UPWIND", "DOWNWIND"]}
                  defaultText={legDisplay()}
                  smallLabel="Point of Sail"
                  size="big"
                  handleSelection={async (item) => {
                    const leg: LegType = item === "REACH" ? "reaching" : item === "UPWIND" ? "upwind" : "downwind";
                    setLegType(leg);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Point of sail changed to ${item}`);
                  }}
                />
                <Show when={!isConfigFilterMode()}>
                  <DropDownButton
                    options={TWS_OPTIONS.map((v) => String(v))}
                    defaultText={twsLabel()}
                    smallLabel="TWS"
                    size="big"
                    handleSelection={async (item) => {
                      const idx = TWS_OPTIONS.indexOf(Number(item));
                      if (idx >= 0) setTwsIndex(idx);
                      const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                      await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `TWS changed to ${item}`);
                    }}
                  />
                </Show>
                <Show when={isConfigFilterMode() && isAdministrator(user())}>
                  <button
                    class="btn cheat-sheet-print-btn"
                    type="button"
                    disabled={excelMode() != null}
                    onClick={() => handleExportCheatSheetExcel("accel")}
                  >
                    {excelMode() === "accel" ? "Exporting…" : "Excel"}
                  </button>
                  <button
                    class="btn cheat-sheet-print-btn"
                    type="button"
                    disabled={excelMode() != null}
                    onClick={() => handleExportCheatSheetExcel("delta")}
                  >
                    {excelMode() === "delta" ? "Exporting…" : "Excel (Δ)"}
                  </button>
                  <Show when={printError()}>
                    <span class="cheat-sheet-print-error">{printError()}</span>
                  </Show>
                </Show>
              </div>
            </div>

            <div class="cheat-sheet-straight-line-section">
            <h1 class="report-page-title cheat-sheet-title">Straight Line</h1>

            <Show when={loading()}>
              <Loading />
            </Show>

            <Show when={!loading() && error()}>
              <div class="cheat-sheet-error">
                {error()}
              </div>
            </Show>

            <Show when={!loading() && !error()}>
              <Show when={!isConfigFilterMode()}>
              <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
                <DropDownButton
                  options={["Channel", "Wind"]}
                  defaultText={groupBy() === "channel" ? "Channel" : "Wind"}
                  smallLabel="Group by"
                  size="big"
                  handleSelection={async (item) => {
                    const val: GroupBy = item === "Wind" ? "wind" : "channel";
                    setGroupBy(val);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Group by changed to ${item}`);
                  }}
                />
                <Show when={groupBy() === "wind"}>
                  <DropDownButton
                    options={METRIC_OPTIONS.map((m) => m.label)}
                    defaultText={METRIC_OPTIONS.find((m) => m.value === selectedMetricKey())?.label ?? "BSP"}
                    smallLabel="Metric"
                    size="big"
                    handleSelection={async (item) => {
                      const opt = METRIC_OPTIONS.find((m) => m.label === item);
                      if (opt) setSelectedMetricKey(opt.value);
                      const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                      await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Metric changed to ${item}`);
                    }}
                  />
                </Show>
              </div>
              </Show>

              <Show when={rows().length > 0}>
                <p class="cheat-sheet-table-explanation">
                  Outliers are removed using IQR (values between the 25th and 75th percentiles). From that cleaned set we take the upper 10% by{" "}
                  {legType() === "reaching" ? "BSP polar%" : "VMG%"} and show the mean per CONFIG.
                  {isConfigFilterMode()
                    ? ` Showing ${selectedConfiguration()} by TWS band [kph].`
                    : groupBy() === "wind"
                      ? " Columns are bins of TWS [kph]."
                      : ""}
                </p>
              </Show>

              <div
                class="copy-table-hover-wrapper"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
              <div class="data-table-container">
                <Show
                  when={rows().length > 0}
                  fallback={
                    <p class="cheat-sheet-empty-message">
                      No cheat sheet data for the current filters. Try a different wind band or point of sail.
                    </p>
                  }
                >
                  <div class="scrollable-container">
                    {(() => {
                      const scales = columnScales();
                      const formatted = formattedColumns();
                      const sortedList = sortedRows();
                      const firstRow = sortedList.length > 0 ? sortedList[0] : null;
                      const deltasEnabled = showDeltas() && !isConfigFilterMode();
                      const deltaClass = (d: number) =>
                        d > 0 ? "race-summary-delta race-summary-delta-positive" : d < 0 ? "race-summary-delta race-summary-delta-negative" : "race-summary-delta";
                      return (
                        <table class="maneuvers-table">
                          <thead>
                            <tr>
                              <For each={tableColumns()}>
                                {(key) => (
                                  <th
                                    class="centered"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => sortData(key)}
                                    title={`Sort by ${getColumnHeader(key)}`}
                                  >
                                    {getColumnHeader(key)} {sortConfig().key === key ? (sortConfig().direction === "asc" ? "▲" : "▼") : ""}
                                  </th>
                                )}
                              </For>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={sortedList}>
                              {(row, index) => {
                                const rowIndex = index();
                                const showDelta = deltasEnabled && firstRow != null && rowIndex > 0;
                                return (
                                  <tr>
                                    <For each={tableColumns()}>
                                      {(key) => {
                                        const scaleClass = cellClassForScale(row, key, scales, formatted);
                                        const val = getRowValue(row, key);
                                        const isRowLabel = isTableRowLabelColumn(key, isConfigFilterMode());
                                        const isRecordCount = key.toLowerCase() === "record_count";
                                        if (isRowLabel) {
                                          return (
                                            <td class={`centered ${scaleClass}`}>
                                              {val != null ? String(val) : "—"}
                                            </td>
                                          );
                                        }
                                        if (isRecordCount) {
                                          return <td class="centered">{formatRecordCount(val)}</td>;
                                        }
                                        const firstVal = firstRow ? getRowValue(firstRow, key) : null;
                                        const delta =
                                          showDelta &&
                                          val != null &&
                                          firstVal != null &&
                                          typeof val === "number" &&
                                          typeof firstVal === "number" &&
                                          !Number.isNaN(val) &&
                                          !Number.isNaN(firstVal)
                                            ? val - firstVal
                                            : null;
                                        const display = val != null ? (typeof val === "number" ? formatNum(val) : String(val)) : "—";
                                        const deltaText = delta != null ? formatDelta(delta) : null;
                                        return (
                                          <td class={`centered ${scaleClass}`}>
                                            {display}
                                            {deltaText != null && <span class={deltaClass(delta!)}>{deltaText}</span>}
                                          </td>
                                        );
                                      }}
                                    </For>
                                  </tr>
                                );
                              }}
                            </For>
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                </Show>
              </div>
              {showCopy() && rows().length > 0 && (
                <div class="copy-table-data-actions">
                  <button
                    type="button"
                    class="copy-table-data-btn"
                    classList={{ "copy-table-data-btn-success": copySuccess() }}
                    onClick={copyTableToClipboard}
                  >
                    {copySuccess() ? "✓ Copied!" : "Copy Table Data"}
                  </button>
                </div>
              )}
              <Show when={rows().length > 0}>
                <div class="flex justify-between items-center mt-2 race-summary-table-actions" style={{ "margin-bottom": "1.5rem" }}>
                  <div class="race-summary-actions-left">
                    <Show when={!isConfigFilterMode()}>
                    <label class="race-summary-deltas-checkbox">
                      <input
                        type="checkbox"
                        checked={showDeltas()}
                        onChange={async (e) => {
                          const checked = e.currentTarget.checked;
                          setShowDeltas(checked);
                          const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                          await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet", `Show deltas ${checked ? "on" : "off"}`);
                        }}
                      />
                      <span>Show deltas</span>
                    </label>
                    </Show>
                  </div>
                  <div class="flex justify-end" style={{ "margin-left": "auto" }}>
                    <button class="btn" type="button" onClick={toggleFormatting}>
                      {formattedColumns().length > 0 ? "Clear Formatting" : "Add formatting"}
                    </button>
                  </div>
                </div>
              </Show>
              </div>
            </Show>
            </div>

              <Show when={showManeuverSections()}>
              <div class="cheat-sheet-maneuvers-section">
                <h2 class="report-page-title cheat-sheet-title">{maneuversSectionTitle()}</h2>

                <Show when={loadingManeuver()}>
                  <Loading />
                </Show>

                <Show when={!loadingManeuver() && errorManeuver()}>
                  <div class="cheat-sheet-error">
                    {errorManeuver()}
                  </div>
                </Show>

                <Show when={!loadingManeuver() && !errorManeuver()}>
                  <Show when={!isConfigFilterMode()}>
                  <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
                    <DropDownButton
                      options={["Channel", "Wind"]}
                      defaultText={groupByManeuver() === "channel" ? "Channel" : "Wind"}
                      smallLabel="Group by"
                      size="big"
                      handleSelection={async (item) => {
                        const val: GroupBy = item === "Wind" ? "wind" : "channel";
                        setGroupByManeuver(val);
                        const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                        await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Maneuvers", `Group by changed to ${item}`);
                      }}
                    />
                    <Show when={groupByManeuver() === "wind"}>
                      <DropDownButton
                        options={MANEUVER_METRIC_OPTIONS.map((m) => m.label)}
                        defaultText={MANEUVER_METRIC_OPTIONS.find((m) => m.value === selectedMetricKeyManeuver())?.label ?? "Drop time"}
                        smallLabel="Metric"
                        size="big"
                        handleSelection={async (item) => {
                          const opt = MANEUVER_METRIC_OPTIONS.find((m) => m.label === item);
                          if (opt) setSelectedMetricKeyManeuver(opt.value);
                          const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                          await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Maneuvers", `Metric changed to ${item}`);
                        }}
                      />
                    </Show>
                  </div>
                  </Show>

                  <Show when={rowsManeuver().length > 0}>
                    <p class="cheat-sheet-table-explanation">
                      Maneuver dynamics and loss metrics for the selected tack/gybe set. From the 25th and 75th percentiles of Loss_total_tgt we take the lowest 10% of loss and show the mean per CONFIG.
                      {isConfigFilterMode()
                        ? ` Showing ${selectedConfiguration()} by TWS band [kph].`
                        : groupByManeuver() === "wind"
                          ? " Columns are bins of TWS [kph]."
                          : ""}
                    </p>
                  </Show>

                  <div
                    class="copy-table-hover-wrapper"
                    onMouseEnter={() => setIsHoveredManeuver(true)}
                    onMouseLeave={() => setIsHoveredManeuver(false)}
                  >
                    <div class="data-table-container">
                      <Show
                        when={rowsManeuver().length > 0}
                        fallback={
                          <p class="cheat-sheet-empty-message">
                            No maneuver cheat sheet data for the current filters. Try a different wind band or maneuver type.
                          </p>
                        }
                      >
                        <div class="scrollable-container">
                          {(() => {
                            const scales = columnScalesManeuver();
                            const formatted = formattedColumnsManeuver();
                            const sortedList = sortedRowsManeuver();
                            const firstRow = sortedList.length > 0 ? sortedList[0] : null;
                            const deltasEnabled = showDeltasManeuver() && !isConfigFilterMode();
                            const deltaClass = (d: number) =>
                              d > 0 ? "race-summary-delta race-summary-delta-positive" : d < 0 ? "race-summary-delta race-summary-delta-negative" : "race-summary-delta";
                            return (
                              <table class="maneuvers-table">
                                <thead>
                                  <tr>
                                    <For each={tableColumnsManeuver()}>
                                      {(key) => (
                                        <th
                                          class="centered"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => sortDataManeuver(key)}
                                          title={`Sort by ${getColumnHeaderManeuver(key)}`}
                                        >
                                          {getColumnHeaderManeuver(key)} {sortConfigManeuver().key === key ? (sortConfigManeuver().direction === "asc" ? "▲" : "▼") : ""}
                                        </th>
                                      )}
                                    </For>
                                  </tr>
                                </thead>
                                <tbody>
                                  <For each={sortedList}>
                                    {(row, index) => {
                                      const rowIndex = index();
                                      const showDelta = deltasEnabled && firstRow != null && rowIndex > 0;
                                      return (
                                        <tr>
                                          <For each={tableColumnsManeuver()}>
                                            {(key) => {
                                              const scaleClass = cellClassForScale(row, key, scales, formatted);
                                              const val = getRowValue(row, key);
                                              const isRowLabel = isTableRowLabelColumn(key, isConfigFilterMode());
                                              const isRecordCount = key.toLowerCase() === "record_count";
                                              if (isRowLabel) {
                                                return (
                                                  <td class={`centered ${scaleClass}`}>
                                                    {val != null ? String(val) : "—"}
                                                  </td>
                                                );
                                              }
                                              if (isRecordCount) {
                                                return <td class="centered">{formatRecordCount(val)}</td>;
                                              }
                                              const firstVal = firstRow ? getRowValue(firstRow, key) : null;
                                              const delta =
                                                showDelta &&
                                                val != null &&
                                                firstVal != null &&
                                                typeof val === "number" &&
                                                typeof firstVal === "number" &&
                                                !Number.isNaN(val) &&
                                                !Number.isNaN(firstVal)
                                                  ? val - firstVal
                                                  : null;
                                              const display = val != null ? (typeof val === "number" ? formatNum(val) : String(val)) : "—";
                                              const deltaText = delta != null ? formatDelta(delta) : null;
                                              return (
                                                <td class={`centered ${scaleClass}`}>
                                                  {display}
                                                  {deltaText != null && <span class={deltaClass(delta!)}>{deltaText}</span>}
                                                </td>
                                              );
                                            }}
                                          </For>
                                        </tr>
                                      );
                                    }}
                                  </For>
                                </tbody>
                              </table>
                            );
                          })()}
                        </div>
                      </Show>
                    </div>
                    {showCopyManeuver() && rowsManeuver().length > 0 && (
                      <div class="copy-table-data-actions">
                        <button
                          type="button"
                          class="copy-table-data-btn"
                          classList={{ "copy-table-data-btn-success": copySuccessManeuver() }}
                          onClick={copyTableToClipboardManeuver}
                        >
                          {copySuccessManeuver() ? "✓ Copied!" : "Copy Table Data"}
                        </button>
                      </div>
                    )}
                    <Show when={rowsManeuver().length > 0}>
                      <div class="flex justify-between items-center mt-2 race-summary-table-actions" style={{ "margin-bottom": "1.5rem" }}>
                        <div class="race-summary-actions-left">
                          <Show when={!isConfigFilterMode()}>
                          <label class="race-summary-deltas-checkbox">
                            <input
                              type="checkbox"
                              checked={showDeltasManeuver()}
                              onChange={async (e) => {
                                const checked = e.currentTarget.checked;
                                setShowDeltasManeuver(checked);
                                const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                                await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Maneuvers", `Show deltas ${checked ? "on" : "off"}`);
                              }}
                            />
                            <span>Show deltas</span>
                          </label>
                          </Show>
                        </div>
                        <div class="flex justify-end" style={{ "margin-left": "auto" }}>
                          <button class="btn" type="button" onClick={toggleFormattingManeuver}>
                            {formattedColumnsManeuver().length > 0 ? "Clear Formatting" : "Add formatting"}
                          </button>
                        </div>
                      </div>
                    </Show>
                  </div>
                </Show>
              </div>
              </Show>

              <Show when={showManeuverSections()}>
              <div class="cheat-sheet-accelerations-section">
                <h2 class="report-page-title cheat-sheet-title">{accelerationsSectionTitle()}</h2>

                <Show when={loadingAccelerations()}>
                  <Loading />
                </Show>

                <Show when={!loadingAccelerations() && errorAccelerations()}>
                  <div class="cheat-sheet-error">
                    {errorAccelerations()}
                  </div>
                </Show>

                <Show when={!loadingAccelerations() && !errorAccelerations()}>
                  <Show when={!isConfigFilterMode()}>
                  <div class="race-summary-dropdown-section cheat-sheet-dropdowns">
                    <DropDownButton
                      options={["Channel", "Wind"]}
                      defaultText={groupByAccelerations() === "channel" ? "Channel" : "Wind"}
                      smallLabel="Group by"
                      size="big"
                      handleSelection={async (item) => {
                        const val: GroupBy = item === "Wind" ? "wind" : "channel";
                        setGroupByAccelerations(val);
                        const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                        await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Accelerations", `Group by changed to ${item}`);
                      }}
                    />
                    <Show when={groupByAccelerations() === "wind"}>
                      <DropDownButton
                        options={ACCELERATIONS_METRIC_OPTIONS.map((m) => m.label)}
                        defaultText={
                          ACCELERATIONS_METRIC_OPTIONS.find((m) => m.value === selectedMetricKeyAccelerations())?.label ??
                          "VMG [%]"
                        }
                        smallLabel="Metric"
                        size="big"
                        handleSelection={async (item) => {
                          const opt = ACCELERATIONS_METRIC_OPTIONS.find((m) => m.label === item);
                          if (opt) setSelectedMetricKeyAccelerations(opt.value);
                          const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                          await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Accelerations", `Metric changed to ${item}`);
                        }}
                      />
                    </Show>
                  </div>
                  </Show>

                  <Show when={rowsAccelerations().length > 0}>
                    <p class="cheat-sheet-table-explanation">
                      Numbers represent values at maximum acceleration on the exit of maneuvers. VMG% is for the complete build phase. Uses the same maneuver set as the Maneuvers table: from the 25th and 75th percentiles of Loss_total_tgt we take the lowest 10% of loss and show the mean per CONFIG.
                      {isConfigFilterMode()
                        ? ` Showing ${selectedConfiguration()} by TWS band [kph].`
                        : groupByAccelerations() === "wind"
                          ? " Columns are bins of TWS [kph]."
                          : ""}
                    </p>
                  </Show>

                  <div
                    class="copy-table-hover-wrapper"
                    onMouseEnter={() => setIsHoveredAccelerations(true)}
                    onMouseLeave={() => setIsHoveredAccelerations(false)}
                  >
                    <div class="data-table-container">
                      <Show
                        when={rowsAccelerations().length > 0}
                        fallback={
                          <p class="cheat-sheet-empty-message">
                            No accelerations cheat sheet data for the current filters. Try a different wind band or maneuver type.
                          </p>
                        }
                      >
                        <div class="scrollable-container">
                          {(() => {
                            const scales = columnScalesAccelerations();
                            const formatted = formattedColumnsAccelerations();
                            const sortedList = sortedRowsAccelerations();
                            const firstRow = sortedList.length > 0 ? sortedList[0] : null;
                            const deltasEnabled = showDeltasAccelerations() && !isConfigFilterMode();
                            const deltaClass = (d: number) =>
                              d > 0 ? "race-summary-delta race-summary-delta-positive" : d < 0 ? "race-summary-delta race-summary-delta-negative" : "race-summary-delta";
                            return (
                              <table class="maneuvers-table">
                                <thead>
                                  <tr>
                                    <For each={tableColumnsAccelerations()}>
                                      {(key) => (
                                        <th
                                          class="centered cheat-sheet-sortable-th"
                                          onClick={() => sortDataAccelerations(key)}
                                          title={`Sort by ${getColumnHeaderAccelerations(key)}`}
                                        >
                                          {getColumnHeaderAccelerations(key)}{" "}
                                          {sortConfigAccelerations().key === key ? (sortConfigAccelerations().direction === "asc" ? "▲" : "▼") : ""}
                                        </th>
                                      )}
                                    </For>
                                  </tr>
                                </thead>
                                <tbody>
                                  <For each={sortedList}>
                                    {(row, index) => {
                                      const rowIndex = index();
                                      const showDelta = deltasEnabled && firstRow != null && rowIndex > 0;
                                      return (
                                        <tr>
                                          <For each={tableColumnsAccelerations()}>
                                            {(key) => {
                                              const scaleClass = cellClassForScale(row, key, scales, formatted);
                                              const val = getRowValue(row, key);
                                              const isRowLabel = isTableRowLabelColumn(key, isConfigFilterMode());
                                              const isRecordCount = key.toLowerCase() === "record_count";
                                              if (isRowLabel) {
                                                return (
                                                  <td class={`centered ${scaleClass}`}>
                                                    {val != null ? String(val) : "—"}
                                                  </td>
                                                );
                                              }
                                              if (isRecordCount) {
                                                return <td class="centered">{formatRecordCount(val)}</td>;
                                              }
                                              const firstVal = firstRow ? getRowValue(firstRow, key) : null;
                                              const delta =
                                                showDelta &&
                                                val != null &&
                                                firstVal != null &&
                                                typeof val === "number" &&
                                                typeof firstVal === "number" &&
                                                !Number.isNaN(val) &&
                                                !Number.isNaN(firstVal)
                                                  ? val - firstVal
                                                  : null;
                                              const display = val != null ? (typeof val === "number" ? formatNum(val) : String(val)) : "—";
                                              const deltaText = delta != null ? formatDelta(delta) : null;
                                              return (
                                                <td class={`centered ${scaleClass}`}>
                                                  {display}
                                                  {deltaText != null && <span class={deltaClass(delta!)}>{deltaText}</span>}
                                                </td>
                                              );
                                            }}
                                          </For>
                                        </tr>
                                      );
                                    }}
                                  </For>
                                </tbody>
                              </table>
                            );
                          })()}
                        </div>
                      </Show>
                    </div>
                    {showCopyAccelerations() && rowsAccelerations().length > 0 && (
                      <div class="copy-table-data-actions">
                        <button
                          type="button"
                          class="copy-table-data-btn"
                          classList={{ "copy-table-data-btn-success": copySuccessAccelerations() }}
                          onClick={copyTableToClipboardAccelerations}
                        >
                          {copySuccessAccelerations() ? "✓ Copied!" : "Copy Table Data"}
                        </button>
                      </div>
                    )}
                    <Show when={rowsAccelerations().length > 0}>
                      <div class="flex justify-between items-center mt-2 race-summary-table-actions cheat-sheet-table-actions-spaced">
                        <div class="race-summary-actions-left">
                          <Show when={!isConfigFilterMode()}>
                          <label class="race-summary-deltas-checkbox">
                            <input
                              type="checkbox"
                              checked={showDeltasAccelerations()}
                              onChange={async (e) => {
                                const checked = e.currentTarget.checked;
                                setShowDeltasAccelerations(checked);
                                const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                                await logActivity(project_id, dataset_id, "CheatSheet.tsx", "Cheat Sheet Accelerations", `Show deltas ${checked ? "on" : "off"}`);
                              }}
                            />
                            <span>Show deltas</span>
                          </label>
                          </Show>
                        </div>
                        <div class="flex justify-end cheat-sheet-table-actions-right">
                          <button class="btn" type="button" onClick={toggleFormattingAccelerations}>
                            {formattedColumnsAccelerations().length > 0 ? "Clear Formatting" : "Add formatting"}
                          </button>
                        </div>
                      </div>
                    </Show>
                  </div>
                </Show>
              </div>
              </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
